import { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { practiceMemberOptions } from '@/entities/practice-member';
import { confirmIdentityOptions, useChangeEmailOptions } from '@/entities/user';
import { grantEmailVerificationGateExemption } from '@/shared/lib/email-verification-gate-exemption';

import { resolveEmailUpdateError } from './resolve-email-update-error';
import { resolveIdentityConfirmationError } from './resolve-identity-confirmation-error';
import { resolveInlineIdentityError } from './resolve-inline-identity-error';

export type EmailChangeStep =
    | { name: 'confirm-identity' }
    | { name: 'update-email' }
    | { name: 'verify'; newEmail: string };

const FIRST_STEP: EmailChangeStep = { name: 'confirm-identity' };

export function useEmailChange() {
    const queryClient = useQueryClient();
    const [stepState, setStepState] = useState<EmailChangeStep | null>(null);
    const confirmIdentityMutation = useMutation(confirmIdentityOptions());
    const changeEmail = useMutation(useChangeEmailOptions());

    const isOpen = stepState !== null;
    const step = stepState ?? FIRST_STEP;
    const identityError = resolveInlineIdentityError(
        confirmIdentityMutation.error,
        changeEmail.error,
    );
    const addressError = resolveEmailUpdateError(changeEmail.error);

    const startEmailChange = () => {
        setStepState(FIRST_STEP);
    };

    const closeEmailChange = () => {
        setStepState(null);
        confirmIdentityMutation.reset();
        changeEmail.reset();
    };

    const confirmIdentity = (currentPassword: string) => {
        changeEmail.reset();

        confirmIdentityMutation.mutate(
            { currentPassword },
            {
                onSuccess: () => setStepState({ name: 'update-email' }),
                onError: (error) => {
                    const identityConfirmationError = resolveIdentityConfirmationError(error);
                    if (identityConfirmationError) {
                        if (identityConfirmationError.surface === 'toast') {
                            toast.error(identityConfirmationError.message);
                        }
                        return;
                    }

                    toast.error('Something went wrong. Please try again.');
                },
            },
        );
    };

    const submitNewEmail = (newEmail: string) => {
        if (step.name !== 'update-email') return;

        changeEmail.mutate(
            { newEmail },
            {
                onSuccess: () => {
                    grantEmailVerificationGateExemption();
                    queryClient.invalidateQueries({ queryKey: practiceMemberOptions().queryKey });
                    setStepState({ name: 'verify', newEmail });
                },
                onError: (error) => {
                    const identityConfirmationError = resolveIdentityConfirmationError(error);
                    if (identityConfirmationError) {
                        setStepState({ name: 'confirm-identity' });
                        if (identityConfirmationError.surface === 'toast') {
                            toast.error(identityConfirmationError.message);
                        }
                        return;
                    }

                    const emailUpdateError = resolveEmailUpdateError(error);
                    if (emailUpdateError) return;

                    toast.error('Something went wrong. Please try again.');
                },
            },
        );
    };

    return {
        isOpen,
        step,
        isConfirmingIdentity: confirmIdentityMutation.isPending,
        isSubmitting: changeEmail.isPending,
        identityError,
        addressError,
        startEmailChange,
        closeEmailChange,
        confirmIdentity,
        submitNewEmail,
    };
}
